import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
import LinearProgress from "@mui/material/LinearProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useState } from "react"

import { useLiveChatWidgetPageContext } from "@contexts/main/live_chat_widget_page_context"
import useMe from "@hooks/use_me"
import type { LiveChatWidgetRelease } from "@models/live_chat_widget"
import { RoleTypes } from "__generated__/graphql"

import CdnTargetCard from "./sub_components/cdn_target_card"
import ConfirmRollbackDialog from "./sub_components/confirm_rollback_dialog"
import LiveFilesTable from "./sub_components/live_files_table"
import PublishPanel from "./sub_components/publish_panel"
import ReleaseHistory from "./sub_components/release_history"

export default function LiveChatWidgetPage() {
  const {
    translate,
    overview,
    releases,
    isLoading,
    busy,
    pendingRelease,
    publish,
    rollback,
    invalidateCache
  } = useLiveChatWidgetPageContext()
  const { me } = useMe()
  const [rollbackTarget, setRollbackTarget] =
    useState<LiveChatWidgetRelease | null>(null)

  // Cosmetic only — the API enforces the ADMIN role on every write.
  const canWrite = me?.role === RoleTypes.Admin

  const confirmRollback = async () => {
    if (!rollbackTarget) return
    const done = await rollback(rollbackTarget._id)
    if (done) setRollbackTarget(null)
  }

  return (
    <Stack gap={2} sx={{ p: 2, width: "100%" }}>
      <Typography variant="h5">
        {translate("live_chat_widget_title")}
      </Typography>

      <Alert severity="info">{translate("live_chat_widget_page_hint")}</Alert>

      {isLoading && <CircularProgress size={24} />}

      {overview?.activeRelease?.status === "INVALIDATION_FAILED" && (
        <Alert severity="warning">
          {translate("live_chat_widget_cache_stuck")}
          {!!overview.activeRelease.errorMessage && (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 0.5, fontFamily: "monospace", wordBreak: "break-word" }}
            >
              {overview.activeRelease.errorMessage}
            </Typography>
          )}
        </Alert>
      )}

      {!!pendingRelease && (
        <Stack gap={0.5}>
          <Alert severity="info">
            {translate("live_chat_widget_invalidation_running")}
          </Alert>
          <LinearProgress />
        </Stack>
      )}

      <CdnTargetCard
        config={overview?.config}
        translate={translate}
        busy={busy}
        canWrite={canWrite}
        onInvalidate={() => void invalidateCache()}
      />

      <PublishPanel
        translate={translate}
        busy={busy}
        canWrite={canWrite}
        liveFiles={overview?.liveFiles ?? []}
        prefix={overview?.config?.prefix ?? ""}
        onPublish={publish}
      />

      <LiveFilesTable
        translate={translate}
        files={overview?.liveFiles ?? []}
        totalBytes={overview?.liveTotalBytes}
      />

      <ReleaseHistory
        translate={translate}
        releases={releases}
        busy={busy}
        canWrite={canWrite}
        onRollback={setRollbackTarget}
      />

      <ConfirmRollbackDialog
        isOpen={!!rollbackTarget}
        setClose={() => setRollbackTarget(null)}
        translate={translate}
        loading={busy}
        release={rollbackTarget}
        onConfirm={confirmRollback}
      />
    </Stack>
  )
}
